export const comprehensiveFinanceCourse = {
  id: "finance-mastery-complete-2024",
  title: "Finance Mastery: Complete Investment & Analysis Course",
  shortDescription: "Complete 24+ hour comprehensive finance course covering everything from fundamentals to advanced portfolio management, derivatives, and corporate finance.",
  description: "Master finance from fundamentals to advanced corporate finance, derivatives, and portfolio management with 24+ hours of comprehensive learning including real-time market analysis, financial modeling, risk management, and practical case studies from real companies.",
  category: "Finance",
  industryCategory: "Financial Services",
  difficulty: "intermediate",
  duration: 24,
  estimatedWeeks: 12,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from certified financial professionals and market analysts",
  thumbnailUrl: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),
  
  learningObjectives: [
    "Analyze financial statements and key performance metrics",
    "Build dynamic financial models and projections",
    "Evaluate stocks using real-time market data",
    "Create balanced investment portfolios",
    "Calculate risk-adjusted returns and portfolio optimization",
    "Master DCF valuation and comparative analysis",
    "Apply modern portfolio theory principles",
    "Develop trading and investment strategies"
  ],

  prerequisites: [
    "Basic Mathematics",
    "Excel/Google Sheets",
    "Economic Fundamentals"
  ],

  skills: [
    "Financial Analysis",
    "Stock Valuation",
    "Portfolio Management", 
    "Risk Assessment",
    "Financial Modeling",
    "Market Analysis",
    "Investment Strategy",
    "Balance Sheet Analysis"
  ],

  tags: [
    "Finance",
    "Investing", 
    "Stocks",
    "Portfolio",
    "Analysis",
    "Markets",
    "Valuation",
    "Risk Management"
  ],

  marketData: {
    averageSalary: 95000,
    salaryRange: {
      min: 65000,
      max: 145000
    },
    jobDemand: 4,
    marketTrend: "growing",
    careerPaths: [
      "Financial Analyst",
      "Portfolio Manager", 
      "Investment Advisor",
      "Risk Manager"
    ],
    relatedSkills: [
      "Excel",
      "Python",
      "Bloomberg Terminal",
      "Financial Modeling",
      "CFA",
      "Statistics"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "finance-fundamentals",
      title: "Financial Fundamentals",
      description: "Master core financial concepts, statements, and analysis techniques",
      order: 1,
      estimatedHours: 3.5,
      content: [
        {
          id: "intro-to-finance",
          title: "Introduction to Finance",
          description: "Understanding financial markets, instruments, and key concepts",
          type: "video",
          duration: 20,
        },
        {
          id: "financial-statements",
          title: "Reading Financial Statements",
          description: "Master income statements, balance sheets, and cash flow statements",
          type: "text",
          duration: 25,
          textContent: `# Financial Statements Mastery

## The Three Core Financial Statements

Financial statements are the backbone of financial analysis. They provide a comprehensive view of a company's financial health and performance. Understanding these statements is crucial for investors, analysts, creditors, and management.

### 1. Income Statement (Profit & Loss Statement)

The Income Statement shows profitability over a specific period (quarter or year):

**Revenue Components:**
- **Gross Revenue**: Total sales before any deductions
- **Net Revenue**: Revenue after returns, allowances, and discounts
- **Operating Revenue**: Revenue from core business operations
- **Non-Operating Revenue**: Interest income, investment gains, etc.

**Cost Structure:**
- **Cost of Goods Sold (COGS)**: Direct costs of producing goods/services
  - Materials, labor, manufacturing overhead
  - For service companies: direct service delivery costs
- **Gross Profit**: Revenue - COGS (measures production efficiency)

**Operating Expenses:**
- **Selling Expenses**: Marketing, sales commissions, advertising
- **General & Administrative (G&A)**: Management salaries, office rent, utilities
- **Research & Development (R&D)**: Innovation and product development costs
- **Depreciation & Amortization**: Allocation of asset costs over time

**Profitability Metrics:**
- **EBITDA**: Earnings Before Interest, Taxes, Depreciation, and Amortization
- **EBIT**: Earnings Before Interest and Taxes (Operating Income)
- **EBT**: Earnings Before Taxes
- **Net Income**: Final profit after all expenses and taxes

### 2. Balance Sheet - The Financial Snapshot

Shows financial position at a specific point in time. Must always balance: **Assets = Liabilities + Shareholders' Equity**

**ASSETS (What the company owns):**

*Current Assets (liquid within 1 year):*
- **Cash and Cash Equivalents**: Most liquid assets
- **Short-term Investments**: Marketable securities
- **Accounts Receivable**: Money owed by customers
- **Inventory**: Raw materials, work-in-progress, finished goods
- **Prepaid Expenses**: Insurance, rent paid in advance
- **Other Current Assets**: Tax refunds, short-term loans to others

*Non-Current Assets (long-term):*
- **Property, Plant & Equipment (PPE)**: Buildings, machinery, equipment
- **Accumulated Depreciation**: Cumulative wear and tear on assets
- **Intangible Assets**: Patents, trademarks, goodwill, software
- **Long-term Investments**: Stocks, bonds held for >1 year
- **Deferred Tax Assets**: Future tax benefits

**LIABILITIES (What the company owes):**

*Current Liabilities (due within 1 year):*
- **Accounts Payable**: Money owed to suppliers
- **Short-term Debt**: Bank loans, commercial paper due <1 year
- **Accrued Expenses**: Wages, interest, taxes owed but not yet paid
- **Deferred Revenue**: Payment received for services not yet provided
- **Current Portion of Long-term Debt**: Principal payments due this year

*Long-term Liabilities:*
- **Long-term Debt**: Bonds, mortgages, bank loans due >1 year
- **Deferred Tax Liabilities**: Future tax obligations
- **Pension Obligations**: Retirement benefits owed to employees
- **Lease Obligations**: Future lease payments

**SHAREHOLDERS' EQUITY (Owner's stake):**
- **Common Stock**: Par value of outstanding shares
- **Additional Paid-in Capital**: Premium paid above par value
- **Retained Earnings**: Accumulated profits not distributed as dividends
- **Accumulated Other Comprehensive Income**: Currency translations, unrealized gains/losses
- **Treasury Stock**: Company's own shares repurchased (reduces equity)

### 3. Cash Flow Statement - The Reality Check

Tracks actual cash movements (not accounting profits). Critical because companies can be profitable but still run out of cash.

**Operating Cash Flow (OCF):**
- Starts with Net Income
- Adjusts for non-cash items (depreciation, amortization)
- Adjusts for working capital changes
- Shows cash from core business operations

**Investing Cash Flow:**
- **Capital Expenditures (CapEx)**: Purchases of PPE
- **Acquisitions**: Buying other companies
- **Asset Sales**: Selling equipment, subsidiaries
- **Investment Purchases/Sales**: Buying/selling securities

**Financing Cash Flow:**
- **Debt Issuance/Repayment**: Borrowing or paying back loans
- **Equity Issuance/Repurchase**: Issuing new shares or buybacks
- **Dividend Payments**: Cash returned to shareholders
- **Interest Payments**: Cost of debt financing

**Free Cash Flow = Operating Cash Flow - Capital Expenditures**
This is the cash available for growth, debt repayment, dividends, or acquisitions.

## Advanced Financial Ratios

### Profitability Analysis

**Margin Analysis:**
- **Gross Margin** = Gross Profit / Revenue
  - Measures production efficiency and pricing power
  - Industry benchmarks: Software (80-90%), Retail (20-30%)
- **Operating Margin** = Operating Income / Revenue
  - Shows operational efficiency after all operating expenses
- **Net Margin** = Net Income / Revenue
  - Bottom-line profitability including all expenses
- **EBITDA Margin** = EBITDA / Revenue
  - Cash generation before financing and accounting decisions

**Return Metrics:**
- **ROE (Return on Equity)** = Net Income / Shareholders' Equity
  - Measures returns generated on shareholders' investments
  - DuPont Formula: ROE = (Net Margin) × (Asset Turnover) × (Equity Multiplier)
- **ROA (Return on Assets)** = Net Income / Total Assets
  - Measures how efficiently assets generate profits
- **ROIC (Return on Invested Capital)** = NOPAT / Invested Capital
  - Measures returns on all invested capital (debt + equity)
- **ROI (Return on Investment)** = (Gain - Cost) / Cost
  - Measures efficiency of specific investments

### Liquidity Analysis (Short-term Financial Health)

- **Current Ratio** = Current Assets / Current Liabilities
  - General benchmark: 1.5-3.0 (varies by industry)
  - Measures ability to pay short-term obligations
- **Quick Ratio (Acid Test)** = (Current Assets - Inventory) / Current Liabilities
  - More conservative liquidity measure
  - Benchmark: >1.0 preferred
- **Cash Ratio** = Cash / Current Liabilities
  - Most conservative liquidity measure
- **Working Capital** = Current Assets - Current Liabilities
  - Absolute measure of short-term liquidity
- **Operating Cash Flow Ratio** = Operating Cash Flow / Current Liabilities
  - Measures cash-based ability to pay short-term debts

### Leverage Analysis (Capital Structure)

- **Debt-to-Equity Ratio** = Total Debt / Total Equity
  - Measures financial leverage
  - Benchmark varies by industry (utilities high, tech low)
- **Debt-to-Assets Ratio** = Total Debt / Total Assets
  - Shows what percentage of assets are financed by debt
- **Interest Coverage Ratio** = EBIT / Interest Expense
  - Measures ability to pay interest on debt
  - Benchmark: >2.5 generally safe
- **Debt Service Coverage Ratio** = Operating Income / Total Debt Service
  - Measures ability to service total debt obligations
- **Equity Multiplier** = Total Assets / Shareholders' Equity
  - Shows financial leverage (higher = more leveraged)
- **Capitalization Ratio** = Long-term Debt / (Long-term Debt + Shareholders' Equity)
  - Measures long-term capital structure

### Efficiency Analysis (Asset Management)

**Turnover Ratios:**
- **Asset Turnover** = Revenue / Average Total Assets
  - Measures how efficiently assets generate revenue
- **Inventory Turnover** = COGS / Average Inventory
  - Days Sales in Inventory = 365 / Inventory Turnover
  - Higher turnover generally better (less capital tied up)
- **Receivables Turnover** = Revenue / Average Accounts Receivable
  - Days Sales Outstanding = 365 / Receivables Turnover
  - Measures collection efficiency
- **Payables Turnover** = COGS / Average Accounts Payable
  - Days Payable Outstanding = 365 / Payables Turnover
  - Measures payment timing to suppliers

**Cash Conversion Cycle:**
DSO + DIO - DPO = Cash Conversion Cycle
- Measures how long cash is tied up in operations
- Shorter cycle = better cash flow management

### Advanced Valuation Ratios

**Market-Based Ratios (for public companies):**
- **Price-to-Earnings (P/E)** = Stock Price / Earnings Per Share
  - Forward P/E uses projected earnings
  - Trailing P/E uses historical earnings
- **Price-to-Book (P/B)** = Stock Price / Book Value Per Share
  - Compares market value to accounting value
- **Price-to-Sales (P/S)** = Market Cap / Revenue
  - Useful for companies without profits
- **Enterprise Value (EV)** = Market Cap + Total Debt - Cash
  - **EV/Revenue**: Enterprise value relative to sales
  - **EV/EBITDA**: Enterprise value relative to cash earnings

## Financial Statement Analysis Framework

### Step 1: Industry Context
- Understand the business model and industry dynamics
- Identify key performance indicators (KPIs) for the industry
- Research industry benchmarks and peer companies

### Step 2: Historical Trend Analysis
- Analyze 5-10 years of historical data
- Look for consistency, growth trends, and cyclical patterns
- Identify inflection points and major changes

### Step 3: Cross-Sectional Analysis
- Compare ratios to industry peers
- Benchmark against best-in-class companies
- Understand competitive positioning

### Step 4: Quality Assessment

**Revenue Quality:**
- Is revenue growing organically or through acquisitions?
- Are sales concentrated among few customers?
- Is revenue recurring or one-time?

**Earnings Quality:**
- Is growth driven by revenue or cost-cutting?
- Are there unusual or non-recurring items?
- How much of earnings is cash vs. accruals?

**Balance Sheet Quality:**
- Is growth funded by debt or equity?
- Are assets productive and well-maintained?
- Any off-balance-sheet liabilities?

### Step 5: Red Flags to Watch

**Income Statement Warning Signs:**
- Declining gross margins without explanation
- Growing revenue but shrinking operating cash flow
- Increasing other income relative to operating income
- Frequent restructuring or unusual charges

**Balance Sheet Warning Signs:**
- Rapid growth in accounts receivable vs. sales
- Growing inventory without corresponding sales growth
- Declining cash despite reported profits
- High debt levels with low interest coverage

**Cash Flow Warning Signs:**
- Negative operating cash flow despite profits
- Increasing gap between net income and operating cash flow
- Heavy reliance on financing to fund operations
- Frequent asset sales to generate cash

### Step 6: Forecasting and Valuation
- Build financial projections based on analysis
- Stress-test assumptions with sensitivity analysis
- Use multiple valuation methods (DCF, comparable company, etc.)
- Consider scenario analysis (base, optimistic, pessimistic)

## Practical Application

Financial statement analysis is most powerful when combined with:
- **Management Discussion & Analysis (MD&A)**: Management's perspective on results
- **Industry Research**: Understanding market trends and competitive dynamics
- **Qualitative Factors**: Management quality, competitive advantages, regulatory environment
- **Macroeconomic Context**: Interest rates, economic cycles, currency impacts

Mastering financial statement analysis is the foundation for all investment and credit decisions. It provides the quantitative backbone for understanding business performance and making informed financial choices.`
        },
        {
          id: "balance-sheet-interactive",
          title: "Balance Sheet Balancing Exercise",
          description: "Interactive exercise to balance a company's balance sheet",
          type: "interactive",
          duration: 30,
          interactiveData: {
            type: "balance_sheet",
            title: "Balance Sheet Balancing Challenge",
            instructions: "Complete the balance sheet by ensuring Assets = Liabilities + Equity. Use the given transactions to update the accounts.",
            initialData: {
              assets: {
                cash: 50000,
                accountsReceivable: 25000,
                inventory: 30000,
                equipment: 100000,
                accumulatedDepreciation: -20000
              },
              liabilities: {
                accountsPayable: 15000,
                notesPayable: 40000,
                longTermDebt: 75000
              },
              equity: {
                commonStock: 40000,
                retainedEarnings: 0 // To be calculated
              }
            },
            transactions: [
              {
                id: 1,
                description: "Purchased inventory with cash",
                amount: 5000,
                accounts: [
                  { name: "inventory", change: 5000 },
                  { name: "cash", change: -5000 }
                ]
              },
              {
                id: 2,
                description: "Collected accounts receivable",
                amount: 8000,
                accounts: [
                  { name: "cash", change: 8000 },
                  { name: "accountsReceivable", change: -8000 }
                ]
              },
              {
                id: 3,
                description: "Recorded monthly depreciation",
                amount: 2000,
                accounts: [
                  { name: "accumulatedDepreciation", change: -2000 },
                  { name: "retainedEarnings", change: -2000 }
                ]
              }
            ],
            expectedOutput: "Total Assets: $183,000 | Total Liabilities + Equity: $183,000"
          }
        }
      ],
      quiz: {
        id: "fundamentals-quiz",
        title: "Financial Fundamentals Quiz",
        description: "Test your understanding of financial statements and ratios",
        timeLimit: 15,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which statement shows a company's profitability over a period of time?",
            options: [
              "Balance Sheet",
              "Income Statement", 
              "Cash Flow Statement",
              "Statement of Equity"
            ],
            correctAnswer: 1,
            explanation: "The Income Statement (P&L) shows revenues, expenses, and profits over a specific time period."
          },
          {
            id: "q2", 
            type: "multiple-choice",
            question: "What does the current ratio measure?",
            options: [
              "Profitability",
              "Liquidity",
              "Leverage", 
              "Efficiency"
            ],
            correctAnswer: 1,
            explanation: "The current ratio (Current Assets / Current Liabilities) measures a company's ability to pay short-term debts."
          },
          {
            id: "q3",
            type: "multiple-choice", 
            question: "In the accounting equation, Assets equals:",
            options: [
              "Liabilities + Equity",
              "Revenue - Expenses",
              "Cash + Investments",
              "Debt + Capital"
            ],
            correctAnswer: 0,
            explanation: "The fundamental accounting equation is Assets = Liabilities + Shareholders' Equity."
          }
        ]
      }
    },

    {
      id: "stock-analysis",
      title: "Stock Analysis & Valuation", 
      description: "Learn to analyze and value stocks using real-time market data",
      order: 2,
      estimatedHours: 3.5,
      content: [
        {
          id: "stock-basics",
          title: "Stock Market Fundamentals",
          description: "Understanding stocks, markets, and trading basics",
          type: "video",
          duration: 25
        },
        {
          id: "valuation-methods",
          title: "Stock Valuation Methods",
          description: "DCF, P/E, PEG, and other valuation techniques",
          type: "video", 
          duration: 35
        },
        {
          id: "real-time-analysis",
          title: "Real-Time Stock Analysis",
          description: "Analyze live stock data and calculate key metrics",
          type: "interactive",
          duration: 40,
          interactiveData: {
            type: "stock_analysis",
            title: "Live Stock Analysis Dashboard",
            instructions: "Select a stock symbol and analyze its real-time data, calculating key financial metrics and ratios.",
            stockSymbols: ["AAPL", "MSFT", "GOOGL", "TSLA", "AMZN", "META", "NVDA"],
            defaultSymbol: "AAPL",
            metrics: [
              "currentPrice",
              "peRatio", 
              "pegRatio",
              "priceToBook",
              "dividendYield",
              "marketCap",
              "beta",
              "eps",
              "revenue",
              "netIncome"
            ],
            analysisFramework: [
              {
                category: "Valuation",
                metrics: ["peRatio", "pegRatio", "priceToBook"],
                interpretation: "Compare to industry averages and historical ranges"
              },
              {
                category: "Financial Health", 
                metrics: ["eps", "revenue", "netIncome"],
                interpretation: "Look for consistent growth trends"
              },
              {
                category: "Risk Assessment",
                metrics: ["beta", "dividendYield"],
                interpretation: "Evaluate risk-return profile"
              }
            ],
            expectedOutput: "Complete analysis showing P/E ratio, growth metrics, and investment recommendation"
          }
        }
      ],
      quiz: {
        id: "stock-analysis-quiz",
        title: "Stock Analysis Quiz",
        description: "Test your stock valuation knowledge",
        timeLimit: 12,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What does a P/E ratio of 25 mean?",
            options: [
              "The stock will return 25% annually",
              "Investors pay $25 for every $1 of earnings",
              "The stock price is $25",
              "The company has 25% profit margin"
            ],
            correctAnswer: 1,
            explanation: "P/E ratio shows how much investors are willing to pay per dollar of annual earnings."
          },
          {
            id: "q2",
            type: "multiple-choice", 
            question: "A PEG ratio below 1.0 typically indicates:",
            options: [
              "Overvalued stock",
              "Undervalued stock given growth rate",
              "High dividend yield",
              "Low risk investment"
            ],
            correctAnswer: 1,
            explanation: "PEG below 1.0 suggests the stock may be undervalued relative to its growth rate."
          }
        ]
      }
    },

    {
      id: "portfolio-management",
      title: "Portfolio Management",
      description: "Build and optimize investment portfolios using modern portfolio theory",
      order: 3,
      estimatedHours: 3,
      content: [
        {
          id: "portfolio-theory",
          title: "Modern Portfolio Theory",
          description: "Understanding diversification, risk, and return optimization",
          type: "video",
          duration: 30
        },
        {
          id: "asset-allocation",
          title: "Asset Allocation Strategies", 
          description: "Strategic and tactical asset allocation approaches",
          type: "text",
          duration: 25,
          textContent: `# Asset Allocation Strategies

## Strategic Asset Allocation

Strategic asset allocation is the long-term approach to portfolio construction based on:
- **Investment Goals**: Growth, income, or preservation
- **Time Horizon**: Years until you need the money
- **Risk Tolerance**: Ability and willingness to accept volatility

### Common Allocation Models

**Conservative (Low Risk)**
- Stocks: 20-40%
- Bonds: 50-70% 
- Alternatives: 5-10%
- Cash: 5-15%

**Moderate (Balanced)**
- Stocks: 50-70%
- Bonds: 25-40%
- Alternatives: 5-15%
- Cash: 0-10%

**Aggressive (High Growth)**
- Stocks: 80-95%
- Bonds: 5-15%
- Alternatives: 5-10%
- Cash: 0-5%

## Tactical Asset Allocation

Tactical allocation involves short-term adjustments based on:
- Market conditions
- Economic cycles
- Valuation levels
- Technical indicators

### Rebalancing Strategies

**Calendar Rebalancing**: Quarterly or annually
**Threshold Rebalancing**: When allocations drift 5-10% from targets
**Combination**: Calendar with threshold triggers

## Asset Classes and Characteristics

### Equities (Stocks)
- **Growth Potential**: Highest long-term returns
- **Volatility**: High short-term fluctuations
- **Liquidity**: Very high for large-cap stocks
- **Inflation Protection**: Good hedge against inflation

### Fixed Income (Bonds)
- **Income Generation**: Steady coupon payments
- **Capital Preservation**: Lower volatility than stocks
- **Interest Rate Risk**: Prices move inverse to rates
- **Credit Risk**: Default risk varies by issuer

### Alternative Investments
- **Real Estate**: REITs provide real estate exposure
- **Commodities**: Inflation hedge, portfolio diversification
- **Private Equity**: Higher returns, limited liquidity
- **Hedge Funds**: Absolute return strategies

## Life-Cycle Investing

### Target Date Approach
Automatically adjusts allocation as you age:
- Age 25: 90% stocks, 10% bonds
- Age 35: 80% stocks, 20% bonds  
- Age 45: 70% stocks, 30% bonds
- Age 55: 60% stocks, 40% bonds
- Age 65: 50% stocks, 50% bonds

### Glide Path Considerations
- **Through Retirement**: Continue aggressive allocation
- **To Retirement**: Become more conservative near retirement
- **Dynamic**: Adjust based on market conditions and personal factors

## Global Diversification

### Geographic Allocation
- **US Markets**: 50-70% of equity allocation
- **International Developed**: 20-30%
- **Emerging Markets**: 5-15%

### Currency Considerations
- **Hedged**: Reduces currency risk
- **Unhedged**: Adds diversification but increases volatility

Effective asset allocation is the most important driver of long-term investment success.`
        },
        {
          id: "portfolio-optimizer",
          title: "Portfolio Optimization Tool",
          description: "Build an optimal portfolio using risk-return analysis",
          type: "interactive",
          duration: 35,
          interactiveData: {
            type: "portfolio_optimizer",
            title: "Portfolio Optimization Challenge",
            instructions: "Build an optimal portfolio by selecting assets and weights to maximize risk-adjusted returns. Use the efficient frontier to find the best risk-return combinations.",
            assets: [
              { symbol: "SPY", name: "S&P 500 ETF", expectedReturn: 0.10, volatility: 0.16, category: "US Equity" },
              { symbol: "VTI", name: "Total Stock Market ETF", expectedReturn: 0.095, volatility: 0.155, category: "US Equity" },
              { symbol: "VEA", name: "International Developed ETF", expectedReturn: 0.08, volatility: 0.18, category: "International" },
              { symbol: "VWO", name: "Emerging Markets ETF", expectedReturn: 0.09, volatility: 0.22, category: "Emerging Markets" },
              { symbol: "BND", name: "Total Bond Market ETF", expectedReturn: 0.04, volatility: 0.05, category: "Fixed Income" },
              { symbol: "VNQ", name: "Real Estate ETF", expectedReturn: 0.07, volatility: 0.20, category: "Real Estate" },
              { symbol: "VGT", name: "Technology ETF", expectedReturn: 0.12, volatility: 0.20, category: "Sector" },
              { symbol: "GLD", name: "Gold ETF", expectedReturn: 0.05, volatility: 0.18, category: "Commodities" }
            ],
            correlationMatrix: [
              [1.00, 0.95, 0.75, 0.70, 0.15, 0.65, 0.85, 0.10],
              [0.95, 1.00, 0.78, 0.72, 0.18, 0.68, 0.82, 0.12],
              [0.75, 0.78, 1.00, 0.85, 0.25, 0.55, 0.70, 0.15],
              [0.70, 0.72, 0.85, 1.00, 0.20, 0.50, 0.65, 0.18],
              [0.15, 0.18, 0.25, 0.20, 1.00, 0.35, 0.10, 0.25],
              [0.65, 0.68, 0.55, 0.50, 0.35, 1.00, 0.60, 0.20],
              [0.85, 0.82, 0.70, 0.65, 0.10, 0.60, 1.00, 0.08],
              [0.10, 0.12, 0.15, 0.18, 0.25, 0.20, 0.08, 1.00]
            ],
            constraints: {
              maxWeight: 0.40,
              minWeight: 0.05,
              totalWeight: 1.00
            },
            objectives: [
              "Maximize Sharpe Ratio",
              "Minimize Risk for 8% Return", 
              "Maximize Return for 12% Risk"
            ],
            expectedOutput: "Portfolio with Sharpe ratio > 0.60 and proper diversification across asset classes"
          }
        }
      ],
      quiz: {
        id: "portfolio-quiz",
        title: "Portfolio Management Quiz", 
        description: "Test your portfolio construction knowledge",
        timeLimit: 10,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the primary benefit of diversification?",
            options: [
              "Increases expected returns",
              "Reduces portfolio risk without sacrificing expected returns",
              "Guarantees positive returns", 
              "Eliminates all investment risk"
            ],
            correctAnswer: 1,
            explanation: "Diversification reduces risk through combining uncorrelated assets without necessarily reducing expected returns."
          }
        ]
      }
    },

    {
      id: "financial-modeling",
      title: "Financial Modeling",
      description: "Build comprehensive financial models and perform DCF analysis", 
      order: 4,
      estimatedHours: 3.5,
      content: [
        {
          id: "dcf-modeling",
          title: "DCF Valuation Model",
          description: "Build discounted cash flow models from scratch",
          type: "video",
          duration: 40
        },
        {
          id: "dcf-interactive",
          title: "Interactive DCF Builder",
          description: "Build a complete DCF model for a real company",
          type: "interactive", 
          duration: 45,
          interactiveData: {
            type: "dcf_model",
            title: "DCF Valuation Model Builder",
            instructions: "Build a comprehensive DCF model by projecting free cash flows and calculating intrinsic value. Use the provided historical data to make reasonable assumptions.",
            company: {
              name: "TechCorp Inc.",
              ticker: "TECH",
              sector: "Technology",
              currentPrice: 150
            },
            historicalData: {
              revenue: [1000, 1150, 1320, 1520, 1750], // Last 5 years in millions
              grossMargin: [0.65, 0.67, 0.68, 0.70, 0.72],
              operatingMargin: [0.15, 0.17, 0.18, 0.20, 0.22],
              capex: [50, 60, 70, 80, 90],
              depreciation: [25, 30, 35, 40, 45],
              workingCapitalChange: [10, 15, 20, 25, 30]
            },
            assumptions: {
              revenueGrowth: [0.20, 0.18, 0.15, 0.12, 0.10], // Next 5 years
              terminalGrowthRate: 0.03,
              wacc: 0.10,
              taxRate: 0.25,
              sharesOutstanding: 100 // millions
            },
            modelComponents: [
              "Revenue Projections",
              "Operating Expense Forecasting", 
              "Free Cash Flow Calculation",
              "Terminal Value Estimation",
              "Present Value Calculation",
              "Intrinsic Value Per Share"
            ],
            expectedOutput: "DCF model showing intrinsic value vs. current market price with investment recommendation"
          }
        }
      ]
    },

    {
      id: "derivatives-risk-management",
      title: "Derivatives & Risk Management",
      description: "Master options, futures, and advanced risk management techniques",
      order: 5,
      estimatedHours: 3,
      content: [
        {
          id: "options-fundamentals",
          title: "Options Trading Fundamentals",
          description: "Understanding calls, puts, and basic option strategies",
          type: "video",
          duration: 35
        },
        {
          id: "options-strategies",
          title: "Advanced Options Strategies",
          description: "Learn spreads, straddles, and complex option positions",
          type: "text",
          duration: 40,
          textContent: `# Advanced Options Strategies

## Single-Leg Strategies

### Long Call
- **Setup**: Buy a call option
- **Outlook**: Bullish on underlying stock
- **Max Profit**: Unlimited above strike + premium
- **Max Loss**: Premium paid
- **Breakeven**: Strike price + premium paid

### Long Put
- **Setup**: Buy a put option
- **Outlook**: Bearish on underlying stock
- **Max Profit**: Strike price - premium paid
- **Max Loss**: Premium paid
- **Breakeven**: Strike price - premium paid

### Covered Call
- **Setup**: Own 100 shares + sell 1 call
- **Outlook**: Neutral to slightly bullish
- **Max Profit**: Strike price - stock cost + premium received
- **Max Loss**: Stock cost - premium received
- **Use Case**: Generate income on existing holdings

### Cash-Secured Put
- **Setup**: Sell put while holding cash equal to 100 shares
- **Outlook**: Neutral to bullish, willing to buy stock
- **Max Profit**: Premium received
- **Max Loss**: Strike price - premium received
- **Use Case**: Get paid while waiting to buy stock

## Spread Strategies

### Bull Call Spread
- **Setup**: Buy lower strike call + sell higher strike call
- **Outlook**: Moderately bullish
- **Max Profit**: Higher strike - lower strike - net premium paid
- **Max Loss**: Net premium paid
- **Breakeven**: Lower strike + net premium paid

### Bear Put Spread
- **Setup**: Buy higher strike put + sell lower strike put
- **Outlook**: Moderately bearish
- **Max Profit**: Higher strike - lower strike - net premium paid
- **Max Loss**: Net premium paid
- **Breakeven**: Higher strike - net premium paid

### Iron Condor
- **Setup**: Sell call spread + sell put spread
- **Outlook**: Neutral (expecting low volatility)
- **Max Profit**: Net premium received
- **Max Loss**: Width of strikes - net premium received
- **Optimal**: Stock stays between short strikes at expiration

### Butterfly Spread
- **Setup**: Buy 1 ITM call + sell 2 ATM calls + buy 1 OTM call
- **Outlook**: Neutral (stock stays near middle strike)
- **Max Profit**: Middle strike - lower strike - net premium paid
- **Max Loss**: Net premium paid
- **Use Case**: Low volatility play with defined risk

## Volatility Strategies

### Long Straddle
- **Setup**: Buy call + buy put (same strike, expiration)
- **Outlook**: High volatility expected (direction unknown)
- **Max Profit**: Unlimited
- **Max Loss**: Total premium paid
- **Breakeven**: Strike ± total premium paid

### Short Straddle
- **Setup**: Sell call + sell put (same strike, expiration)
- **Outlook**: Low volatility expected
- **Max Profit**: Total premium received
- **Max Loss**: Unlimited
- **Breakeven**: Strike ± total premium received

### Strangle
- **Setup**: Buy OTM call + buy OTM put
- **Outlook**: High volatility expected
- **Max Profit**: Unlimited
- **Max Loss**: Total premium paid
- **Advantage**: Cheaper than straddle, need bigger move

## Risk Management Principles

### Position Sizing
- Never risk more than 1-2% of portfolio on single trade
- Consider correlation between positions
- Account for potential assignment on short options

### Greeks Management
- **Delta**: Directional exposure (0-1 for calls, 0 to -1 for puts)
- **Gamma**: Rate of delta change (higher for ATM options)
- **Theta**: Time decay (works against long options)
- **Vega**: Volatility sensitivity (higher for longer-dated options)
- **Rho**: Interest rate sensitivity (minimal for most trades)

### Exit Strategies
- Set profit targets (often 25-50% of max profit)
- Define stop losses before entering
- Consider early assignment risk on short options
- Roll positions when necessary to manage risk

## Futures Contracts

### Contract Specifications
- **Size**: Standard contract size (e.g., 5,000 bushels corn)
- **Tick Size**: Minimum price movement
- **Margin**: Initial and maintenance margin requirements
- **Delivery**: Physical delivery vs. cash settlement

### Popular Futures Markets
- **Equity Index**: S&P 500 (ES), Nasdaq (NQ), Russell 2000 (RTY)
- **Currency**: EUR/USD, GBP/USD, USD/JPY
- **Commodities**: Crude oil, gold, silver, agricultural products
- **Interest Rates**: Treasury bonds, Eurodollar

### Hedging with Futures
- **Long Hedge**: Buy futures to protect against rising prices
- **Short Hedge**: Sell futures to protect against falling prices
- **Cross Hedge**: Use related futures when direct hedge unavailable
- **Basis Risk**: Risk that futures and cash prices don't move perfectly together

## Risk Metrics and Management

### Value at Risk (VaR)
- Measures potential loss at given confidence level
- **Parametric VaR**: Assumes normal distribution
- **Historical VaR**: Based on historical returns
- **Monte Carlo VaR**: Uses simulated scenarios

### Expected Shortfall (ES)
- Average loss beyond VaR threshold
- Better measure of tail risk than VaR
- Also known as Conditional Value at Risk (CVaR)

### Portfolio Risk Management
- **Diversification**: Spread risk across assets, sectors, geographies
- **Correlation Analysis**: Monitor changing relationships between assets
- **Stress Testing**: Test portfolio under extreme scenarios
- **Dynamic Hedging**: Adjust hedges as market conditions change

Understanding derivatives and risk management is crucial for sophisticated portfolio management and downside protection.`
        },
        {
          id: "risk-calculator",
          title: "Options Risk Calculator",
          description: "Calculate option Greeks and analyze risk/reward profiles",
          type: "interactive",
          duration: 35,
          interactiveData: {
            type: "options_calculator",
            title: "Options Risk & Profit Calculator",
            instructions: "Calculate option values, Greeks, and profit/loss scenarios for different strategies.",
            strategies: [
              "Long Call", "Long Put", "Covered Call", "Cash-Secured Put",
              "Bull Call Spread", "Bear Put Spread", "Iron Condor", "Long Straddle"
            ],
            parameters: {
              stockPrice: 100,
              strikePrice: 100,
              timeToExpiration: 30,
              volatility: 0.25,
              interestRate: 0.05,
              dividendYield: 0.02
            },
            calculations: [
              "Option Price (Black-Scholes)",
              "Delta", "Gamma", "Theta", "Vega",
              "Profit/Loss at Expiration",
              "Breakeven Points",
              "Maximum Risk/Reward"
            ],
            expectedOutput: "Complete options analysis with risk metrics and profit/loss scenarios"
          }
        }
      ],
      quiz: {
        id: "derivatives-quiz",
        title: "Derivatives & Risk Management Quiz",
        description: "Test your knowledge of options, futures, and risk management",
        timeLimit: 15,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the maximum loss for a long call option?",
            options: [
              "Unlimited",
              "The premium paid",
              "The strike price",
              "Zero"
            ],
            correctAnswer: 1,
            explanation: "The maximum loss for a long call is limited to the premium paid for the option."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Which Greek measures an option's sensitivity to time decay?",
            options: [
              "Delta",
              "Gamma",
              "Theta",
              "Vega"
            ],
            correctAnswer: 2,
            explanation: "Theta measures time decay - how much an option's value decreases as time passes."
          }
        ]
      }
    },

    {
      id: "corporate-finance",
      title: "Corporate Finance",
      description: "Capital structure, M&A, and corporate financial decision making",
      order: 6,
      estimatedHours: 3.5,
      content: [
        {
          id: "capital-structure",
          title: "Capital Structure Optimization",
          description: "Understanding debt vs equity financing and optimal capital structure",
          type: "text",
          duration: 45,
          textContent: `# Capital Structure Optimization

## Fundamentals of Capital Structure

Capital structure refers to the mix of debt and equity financing a company uses to fund its operations and growth. The optimal capital structure maximizes firm value while minimizing the cost of capital.

### Key Components

**Equity Financing:**
- **Common Stock**: Voting rights, residual claims, unlimited upside
- **Preferred Stock**: Fixed dividends, priority over common stock
- **Retained Earnings**: Internal funding from accumulated profits

**Debt Financing:**
- **Bank Loans**: Negotiated terms, relationship-based
- **Corporate Bonds**: Market-traded, standardized terms
- **Commercial Paper**: Short-term, high-grade companies only
- **Leases**: Operating vs. finance leases

## Theoretical Frameworks

### Modigliani-Miller Theorem

**Without Taxes (MM I):**
- Capital structure is irrelevant to firm value
- Cost of equity increases with leverage to offset cheaper debt
- **Formula**: V_L = V_U (leveraged firm value = unleveraged firm value)

**With Taxes (MM II):**
- Debt creates value through tax shield
- **Formula**: V_L = V_U + T_C × D (where T_C = corporate tax rate, D = debt)
- **Tax Shield**: Interest payments are tax-deductible

### Trade-off Theory
Optimal capital structure balances:
- **Benefits of Debt**: Tax shield, discipline on management
- **Costs of Debt**: Financial distress, bankruptcy costs, agency costs

### Pecking Order Theory
Companies prefer financing in this order:
1. **Internal Financing**: Retained earnings (lowest cost)
2. **Debt Financing**: External debt (information asymmetry costs)
3. **Equity Financing**: New equity (highest cost due to adverse selection)

### Market Timing Theory
- Companies issue equity when stock price is high
- Companies buy back shares when stock price is low
- Capital structure reflects historical market timing decisions

## Cost of Capital Calculations

### Weighted Average Cost of Capital (WACC)
**Formula**: WACC = (E/V × R_E) + (D/V × R_D × (1-T_C))

Where:
- E = Market value of equity
- D = Market value of debt  
- V = E + D (total firm value)
- R_E = Cost of equity
- R_D = Cost of debt
- T_C = Corporate tax rate

### Cost of Equity Estimation

**Capital Asset Pricing Model (CAPM):**
R_E = R_f + β × (R_m - R_f)
- R_f = Risk-free rate
- β = Beta (systematic risk)
- R_m = Market return
- (R_m - R_f) = Market risk premium

**Dividend Growth Model:**
R_E = (D_1/P_0) + g
- D_1 = Next year's expected dividend
- P_0 = Current stock price
- g = Dividend growth rate

**Build-up Method:**
R_E = Risk-free rate + Equity risk premium + Size premium + Company-specific premium

### Cost of Debt Estimation
- **Current Yield**: Based on existing debt market prices
- **Credit Rating Approach**: Risk-free rate + credit spread
- **Synthetic Rating**: Estimate rating based on financial metrics

## Capital Structure Decisions

### Industry Analysis
Different industries have different optimal capital structures:

**High Debt Capacity:**
- Utilities (stable cash flows, regulated)
- Real estate (asset-backed, tax benefits)
- Mature industries (predictable earnings)

**Low Debt Capacity:**
- Technology (volatile cash flows, growth options)
- Pharmaceuticals (R&D intensive, regulatory risk)
- Cyclical industries (earnings volatility)

### Financial Flexibility
- Maintain debt capacity for future opportunities
- Consider credit rating implications
- Access to capital markets during stress periods

### Agency Considerations

**Debt-Equity Conflicts:**
- **Asset Substitution**: Equity holders prefer riskier projects
- **Underinvestment**: Debt overhang reduces incentive to invest
- **Dividend Policy**: Conflicts over cash distribution

**Solutions:**
- **Protective Covenants**: Restrict management actions
- **Convertible Bonds**: Align debt and equity interests
- **Performance-based Compensation**: Align management with shareholders

## Optimal Capital Structure Analysis

### Factors Favoring Debt
- High marginal tax rates (tax shield benefit)
- Stable, predictable cash flows
- Low business risk
- Tangible assets (good collateral)
- Low growth opportunities
- Professional management

### Factors Favoring Equity
- High business risk/cash flow volatility
- Intangible assets
- High growth opportunities
- Low marginal tax rates
- Need for financial flexibility
- High bankruptcy costs

### Dynamic Considerations
- **Life Cycle**: Young firms rely more on equity, mature firms use more debt
- **Market Conditions**: Issue equity in bull markets, debt in bear markets
- **Competitive Environment**: Maintain flexibility in uncertain times

## Capital Structure Ratios

### Leverage Ratios
- **Debt-to-Equity**: Total Debt / Total Equity
- **Debt-to-Assets**: Total Debt / Total Assets
- **Long-term Debt-to-Capital**: LT Debt / (LT Debt + Equity)

### Coverage Ratios
- **Interest Coverage**: EBIT / Interest Expense
- **Fixed Charge Coverage**: (EBIT + Lease) / (Interest + Lease)
- **Cash Coverage**: (EBIT + Depreciation) / Interest Expense

### Market-Based Ratios
- **Market Debt-to-Equity**: Use market values instead of book values
- **Enterprise Value**: Market Cap + Net Debt

## Practical Applications

### Capital Structure Planning
1. **Analyze Industry Benchmarks**: Compare to peer companies
2. **Assess Business Risk**: Evaluate cash flow stability
3. **Consider Growth Plans**: Future financing needs
4. **Tax Optimization**: Maximize after-tax value
5. **Rating Agency Criteria**: Maintain target credit rating
6. **Covenant Compliance**: Ensure debt agreement compliance

### Restructuring Scenarios
- **Recapitalization**: Change debt/equity mix through buybacks or dividends
- **Refinancing**: Replace existing debt with new debt
- **Financial Distress**: Debt restructuring, equity infusion
- **LBO**: High leverage to finance acquisition

Understanding capital structure optimization helps companies minimize their cost of capital and maximize firm value while maintaining adequate financial flexibility.`
        },
        {
          id: "ma-analysis",
          title: "Mergers & Acquisitions Analysis",
          description: "Valuation methods, deal structures, and M&A financial modeling",
          type: "video",
          duration: 50
        },
        {
          id: "lbo-modeling",
          title: "Leveraged Buyout Modeling",
          description: "Build a complete LBO model and analyze returns",
          type: "interactive",
          duration: 45,
          interactiveData: {
            type: "lbo_model",
            title: "LBO Model Builder",
            instructions: "Build a leveraged buyout model to analyze acquisition returns and debt paydown.",
            company: {
              name: "RetailCorp",
              sector: "Consumer Retail",
              revenue: 1000,
              ebitda: 150,
              capex: 25,
              workingCapital: 50
            },
            transaction: {
              purchasePrice: 900,
              equityInvestment: 270,
              debtFinancing: 630,
              transactionFees: 15
            },
            assumptions: {
              revenueGrowth: [0.05, 0.04, 0.04, 0.03, 0.03],
              ebitdaMargin: [0.15, 0.16, 0.17, 0.17, 0.18],
              exitMultiple: 6.0,
              holdingPeriod: 5
            },
            expectedOutput: "LBO model showing IRR, cash-on-cash return, and debt paydown schedule"
          }
        }
      ],
      quiz: {
        id: "corporate-finance-quiz",
        title: "Corporate Finance Quiz",
        description: "Test your understanding of capital structure and corporate finance",
        timeLimit: 12,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the tax benefit of debt financing?",
            options: [
              "Dividends are tax deductible",
              "Interest payments are tax deductible",
              "Debt is cheaper than equity",
              "Debt has no risk"
            ],
            correctAnswer: 1,
            explanation: "Interest payments on debt are tax-deductible, creating a tax shield that reduces the effective cost of debt."
          }
        ]
      }
    },

    {
      id: "fixed-income-securities",
      title: "Fixed Income Securities",
      description: "Bond analysis, yield curves, and fixed income portfolio management",
      order: 7,
      estimatedHours: 2.5,
      content: [
        {
          id: "bond-fundamentals",
          title: "Bond Valuation and Analysis",
          description: "Understanding bond pricing, duration, and convexity",
          type: "text",
          duration: 35,
          textContent: `# Fixed Income Securities Analysis

## Bond Fundamentals

### Basic Bond Components
- **Face Value (Par)**: Principal amount paid at maturity ($1,000 typical)
- **Coupon Rate**: Annual interest rate (e.g., 5% = $50/year on $1,000 bond)
- **Maturity**: Time until principal repayment
- **Credit Quality**: Issuer's ability to make payments (AAA to D rating)
- **Call Features**: Right for issuer to redeem early

### Bond Pricing Formula
**Present Value of Cash Flows:**
P = Σ[C/(1+r)^t] + FV/(1+r)^n

Where:
- P = Bond price
- C = Coupon payment
- r = Required yield (discount rate)
- t = Time period
- FV = Face value
- n = Number of periods to maturity

### Yield Measures

**Current Yield** = Annual Coupon / Current Price
- Simple income return measure
- Doesn't consider capital gains/losses

**Yield to Maturity (YTM)**
- Internal rate of return if held to maturity
- Accounts for all cash flows and capital appreciation/depreciation
- Most comprehensive yield measure

**Yield to Call (YTC)**
- Return if bond called at first call date
- Important for callable bonds trading above par

**Yield to Worst**
- Lower of YTM or YTC
- Conservative measure for callable bonds

## Duration and Convexity

### Duration Concepts

**Macaulay Duration**
- Weighted average time to receive cash flows
- Measures bond's maturity in present value terms
- Formula: D = Σ[t × PV(CF_t)] / Bond Price

**Modified Duration**
- Price sensitivity to yield changes
- Modified Duration = Macaulay Duration / (1 + YTM)
- **Rule**: 1% yield change = Modified Duration % price change (opposite direction)

**Effective Duration**
- Price sensitivity measured using actual price changes
- Better for bonds with embedded options
- Formula: (P_- - P_+) / (2 × P_0 × Δy)

### Duration Applications

**Portfolio Duration**
- Weighted average of individual bond durations
- Weights based on market values
- Measures portfolio interest rate sensitivity

**Duration Matching**
- Immunization strategy
- Match portfolio duration to investment horizon
- Protects against interest rate risk

### Convexity
- Measures curvature of price-yield relationship
- Duration assumes linear relationship (approximation)
- Convexity corrects for this assumption

**Price Change Formula:**
ΔP/P ≈ -Modified Duration × Δy + 0.5 × Convexity × (Δy)²

**Positive Convexity**: Price increases more for yield decreases than it falls for yield increases
**Negative Convexity**: Callable bonds when rates fall below coupon rate

## Yield Curve Analysis

### Yield Curve Shapes

**Normal (Upward Sloping)**
- Long-term rates > short-term rates
- Reflects term premium for duration risk
- Typical economic environment

**Inverted (Downward Sloping)**
- Short-term rates > long-term rates
- Often predicts recession
- Central bank fighting inflation

**Flat**
- Similar rates across maturities
- Transition period between normal and inverted
- Economic uncertainty

**Humped**
- Medium-term rates highest
- Less common shape
- Unusual monetary policy environment

### Theories of Term Structure

**Pure Expectations Theory**
- Long-term rates = average of expected future short-term rates
- No risk premium for maturity
- Yield curve predicts future interest rates

**Liquidity Premium Theory**
- Investors demand premium for longer maturity
- Long rates = expected future short rates + liquidity premium
- Explains typical upward slope

**Market Segmentation Theory**
- Different investors have preferred maturities
- Supply and demand in each segment determines rates
- Limited arbitrage between segments

## Credit Analysis

### Credit Risk Components

**Default Risk**
- Probability issuer cannot make payments
- Measured by credit ratings (Moody's, S&P, Fitch)
- Higher risk = higher yield spread over Treasuries

**Recovery Risk**
- Amount recovered in default scenario
- Senior debt recovers more than subordinated
- Secured debt recovers more than unsecured

**Credit Spread Risk**
- Risk that credit spreads widen
- Even without default, prices can fall
- Correlated with economic conditions

### Credit Rating Analysis

**Investment Grade (BBB-/Baa3 and above)**
- Low default risk
- Eligible for many institutional portfolios
- Narrower credit spreads

**High Yield (BB+/Ba1 and below)**
- Higher default risk
- Higher yields to compensate
- More volatile prices

**Rating Factors**
- Financial ratios (leverage, coverage, profitability)
- Business risk (industry, competition, regulation)
- Management quality and strategy
- Economic and political environment

## Fixed Income Strategies

### Passive Strategies

**Buy and Hold**
- Purchase bonds and hold to maturity
- Eliminates interest rate risk if held to maturity
- Subject to credit risk and inflation risk

**Indexing**
- Match performance of bond index
- Diversified exposure
- Low cost, consistent with market returns

### Active Strategies

**Interest Rate Anticipation**
- Adjust duration based on rate expectations
- Increase duration if rates expected to fall
- Decrease duration if rates expected to rise

**Credit Analysis**
- Identify undervalued credits
- Buy improving credits, sell deteriorating credits
- Requires fundamental credit research

**Yield Curve Strategies**
- **Bullet**: Concentrate holdings in one maturity
- **Barbell**: Holdings at short and long ends
- **Ladder**: Equal amounts across maturities

## Municipal Bonds

### Tax Considerations
- Interest generally exempt from federal taxes
- May be exempt from state/local taxes
- **Taxable Equivalent Yield** = Tax-free yield / (1 - tax rate)

### Types of Municipal Bonds
- **General Obligation**: Backed by taxing power
- **Revenue Bonds**: Backed by specific revenue source
- **Industrial Development**: Benefits private entities

## International Bonds

### Currency Risk
- Foreign bonds subject to exchange rate risk
- Can hedge currency exposure
- Adds diversification to portfolio

### Types
- **Foreign Bonds**: Issued in foreign market, foreign currency
- **Eurobonds**: Issued outside issuer's home country
- **Global Bonds**: Issued simultaneously in multiple markets

Fixed income securities provide steady income and diversification benefits, but require careful analysis of credit, interest rate, and inflation risks.`
        },
        {
          id: "yield-curve-modeling",
          title: "Yield Curve Analysis Tool",
          description: "Analyze yield curves and calculate bond metrics",
          type: "interactive",
          duration: 30,
          interactiveData: {
            type: "yield_curve_analysis",
            title: "Yield Curve & Bond Analysis",
            instructions: "Analyze different yield curve shapes and calculate bond pricing and risk metrics.",
            bondParameters: {
              faceValue: 1000,
              couponRate: 0.05,
              maturity: 10,
              creditSpread: 0.01
            },
            yieldCurveShapes: ["Normal", "Inverted", "Flat", "Humped"],
            calculations: [
              "Bond Price",
              "Yield to Maturity",
              "Modified Duration",
              "Convexity",
              "Price Change for +100bp yield shock"
            ],
            expectedOutput: "Complete bond analysis with duration and convexity metrics"
          }
        }
      ]
    },

    {
      id: "behavioral-finance",
      title: "Behavioral Finance",
      description: "Psychology of financial markets and investor behavior",
      order: 8,
      estimatedHours: 2,
      content: [
        {
          id: "cognitive-biases",
          title: "Cognitive Biases in Finance",
          description: "Understanding psychological traps that affect investment decisions",
          type: "text",
          duration: 30,
          textContent: `# Behavioral Finance: Cognitive Biases and Market Psychology

## Cognitive Biases in Investment Decision Making

Behavioral finance combines psychology and economics to explain why investors often make irrational decisions that deviate from traditional finance theory.

### Overconfidence Bias

**Definition**: Tendency to overestimate one's knowledge, abilities, or chances of success

**Investment Impact:**
- Excessive trading (reduces returns due to costs and poor timing)
- Under-diversification (concentrated positions in "sure things")
- Failure to conduct adequate research
- Ignoring contrary evidence

**Example**: Day traders who believe they can consistently beat the market despite evidence that most lose money

**Mitigation Strategies:**
- Keep detailed records of investment decisions and outcomes
- Seek contrary viewpoints before making decisions
- Use systematic, rule-based investment approaches

### Confirmation Bias

**Definition**: Tendency to search for, interpret, and recall information that confirms pre-existing beliefs

**Investment Impact:**
- Selective information gathering
- Ignoring negative news about owned stocks
- Echo chamber effect in investment communities
- Delayed recognition of changing fundamentals

**Example**: Tesla investor only reading bullish Tesla analysis while ignoring competitive threats

**Mitigation Strategies:**
- Actively seek disconfirming evidence
- Use devil's advocate approach
- Diversify information sources

### Anchoring Bias

**Definition**: Over-reliance on first piece of information encountered (the "anchor")

**Investment Impact:**
- Using irrelevant reference points for valuation
- Difficulty adjusting from initial price targets
- 52-week high/low becomes arbitrary anchor
- IPO pricing influences long-term valuation perception

**Example**: Stock purchased at $100 seems "cheap" at $90, even if fundamentals have deteriorated

**Mitigation Strategies:**
- Use multiple valuation methods
- Regularly reassess assumptions
- Focus on intrinsic value, not historical prices

### Loss Aversion

**Definition**: People feel losses roughly twice as strongly as equivalent gains

**Investment Impact:**
- Reluctance to realize losses (disposition effect)
- Holding losing positions too long
- Taking profits too early
- Excessive risk aversion in portfolio construction

**Example**: Holding a stock down 50% hoping to "break even" instead of cutting losses

**Mitigation Strategies:**
- Set stop-loss levels in advance
- Use systematic rebalancing
- Frame decisions in terms of future prospects, not past performance

### Mental Accounting

**Definition**: Treating money differently based on arbitrary categories

**Investment Impact:**
- Treating "house money" (gains) differently than principal
- Separate "safe" and "risky" buckets instead of total portfolio view
- Tax inefficient decisions
- Failure to consider opportunity costs

**Example**: Being conservative with 401k but speculative with separate trading account

**Mitigation Strategies:**
- View all investments as part of total portfolio
- Consider after-tax returns
- Regular portfolio rebalancing across all accounts

### Herding Behavior

**Definition**: Tendency to follow the crowd and conform to group behavior

**Investment Impact:**
- Momentum bubbles and crashes
- Performance chasing (buying high, selling low)
- Fear of missing out (FOMO) on popular investments
- Panic selling during market downturns

**Example**: Buying technology stocks in 1999 or real estate in 2006 because "everyone was doing it"

**Mitigation Strategies:**
- Contrarian thinking when sentiment becomes extreme
- Stick to long-term investment plan
- Dollar-cost averaging to reduce timing risk

### Availability Bias

**Definition**: Judging probability of events by how easily examples come to mind

**Investment Impact:**
- Overweighting recent or memorable events
- Recency bias in return expectations
- Overestimating "black swan" event probabilities after they occur
- Media coverage influences perceived importance

**Example**: Avoiding airline stocks after plane crash, despite statistical safety

**Mitigation Strategies:**
- Use base rates and historical data
- Consider broader statistical evidence
- Recognize media coverage bias

### Representativeness Bias

**Definition**: Judging probability based on similarity to familiar patterns

**Investment Impact:**
- Extrapolating small samples to populations
- Believing past performance predicts future results
- Seeing patterns in random data
- Stereotyping based on limited characteristics

**Example**: Assuming a company with five years of growth will continue growing indefinitely

**Mitigation Strategies:**
- Consider sample sizes and statistical significance
- Understand reversion to mean
- Separate correlation from causation

## Market Anomalies Explained by Behavioral Finance

### Momentum Effect
- Stocks with strong recent performance continue outperforming short-term
- Explained by herding, confirmation bias, and under-reaction to news
- Eventually reverses as fundamentals matter long-term

### Value Effect
- Value stocks (low P/E, P/B) outperform growth stocks long-term
- May result from overconfidence in growth projections
- Loss aversion causes overreaction to bad news for value stocks

### Size Effect
- Small-cap stocks have historically outperformed large-cap
- May reflect neglect by institutional investors
- Higher risk may not be fully captured by CAPM beta

### Calendar Effects
- **January Effect**: Small stocks perform well in January
- **Weekend Effect**: Returns often negative on Mondays
- May result from behavioral patterns and institutional constraints

## Behavioral Portfolio Theory

### Traditional vs. Behavioral Approach

**Traditional (Mean-Variance Optimization):**
- Single optimal portfolio for each risk level
- All investors hold combination of market portfolio and risk-free asset
- Focus on risk-adjusted returns

**Behavioral (Mental Accounting):**
- Investors create multiple mental accounts
- Different risk tolerances for different accounts
- Satisficing rather than optimizing

### Behavioral Asset Pricing

**Sentiment-Based Models:**
- Investor sentiment affects prices
- Noise traders create mispricing opportunities
- Arbitrage limits prevent quick correction

**Adaptive Market Hypothesis:**
- Market efficiency varies over time
- Depends on number of participants and market conditions
- Opportunities exist but evolve as markets adapt

## Practical Applications

### Investment Process Improvements

**Decision Frameworks:**
- Use checklists to ensure consistent analysis
- Pre-commitment strategies (setting rules in advance)
- Regular portfolio reviews independent of recent performance

**Risk Management:**
- Position sizing based on conviction and correlation
- Systematic rebalancing schedules
- Stress testing for behavioral responses

### Client Management (for Advisors)

**Understanding Client Psychology:**
- Risk tolerance vs. risk capacity
- Time horizon vs. emotional patience
- Goals-based vs. benchmark-relative mindset

**Communication Strategies:**
- Frame decisions appropriately
- Educate about behavioral biases
- Set realistic expectations about volatility

### Market Analysis

**Sentiment Indicators:**
- VIX (fear index)
- Put/call ratios
- Insider buying/selling
- Margin debt levels

**Contrarian Indicators:**
- Extreme optimism/pessimism in surveys
- Magazine covers as contrary indicators
- Analyst recommendation extremes

Understanding behavioral finance helps investors make better decisions by recognizing and mitigating the psychological biases that can derail investment success. While markets may not always be perfectly efficient, systematic approaches can help capture opportunities created by behavioral inefficiencies.`
        },
        {
          id: "market-psychology",
          title: "Market Cycles and Investor Psychology",
          description: "Understanding market bubbles, crashes, and crowd behavior",
          type: "video",
          duration: 25
        },
        {
          id: "bias-assessment",
          title: "Investment Bias Self-Assessment",
          description: "Identify your own behavioral biases and create mitigation strategies",
          type: "interactive",
          duration: 25,
          interactiveData: {
            type: "bias_assessment",
            title: "Behavioral Finance Bias Assessment",
            instructions: "Complete scenarios to identify your behavioral biases and learn mitigation strategies.",
            scenarios: [
              {
                id: "overconfidence",
                title: "Trading Frequency Scenario",
                description: "You've had three successful stock picks in a row. What do you do next?",
                options: [
                  "Increase position sizes - I'm on a roll",
                  "Look for more trading opportunities",
                  "Stick to my original investment plan",
                  "Take some profits and reassess"
                ],
                biases: ["Overconfidence", "Hot-hand fallacy"],
                correctAnswer: 2
              },
              {
                id: "loss_aversion",
                title: "Loss Recognition Scenario",
                description: "A stock you bought at $100 is now $70. Fundamentals haven't changed. What do you do?",
                options: [
                  "Hold until it gets back to $100",
                  "Buy more since it's cheaper",
                  "Sell and move on",
                  "Set a stop-loss at current level"
                ],
                biases: ["Loss aversion", "Anchoring"],
                correctAnswer: 2
              }
            ],
            expectedOutput: "Personalized bias profile with specific mitigation strategies"
          }
        }
      ]
    },

    {
      id: "advanced-topics",
      title: "Advanced Finance Topics",
      description: "Quantitative finance, alternative investments, and emerging trends",
      order: 9,
      estimatedHours: 2.5,
      content: [
        {
          id: "quantitative-methods",
          title: "Quantitative Finance Methods",
          description: "Statistical methods, Monte Carlo simulation, and factor models",
          type: "text",
          duration: 35,
          textContent: `# Advanced Quantitative Finance Methods

## Statistical Methods in Finance

### Time Series Analysis

**Autoregressive (AR) Models**
- Current value depends on past values
- AR(1): X_t = α + βX_{t-1} + ε_t
- Used for forecasting returns and volatility

**Moving Average (MA) Models**
- Current value depends on past error terms
- MA(1): X_t = α + ε_t + θε_{t-1}
- Captures temporary shocks to returns

**ARIMA Models**
- Combines autoregressive, integrated, and moving average components
- ARIMA(p,d,q) where p=AR terms, d=differencing, q=MA terms
- Box-Jenkins methodology for model identification

**GARCH Models (Generalized Autoregressive Conditional Heteroskedasticity)**
- Models time-varying volatility
- GARCH(1,1): σ²_t = α + βσ²_{t-1} + γε²_{t-1}
- Captures volatility clustering in financial markets

### Monte Carlo Simulation

**Basic Principles:**
- Use random number generation to model uncertainty
- Run thousands of scenarios to estimate distributions
- Particularly useful for complex derivatives and portfolio risk

**Applications in Finance:**
- **Option Pricing**: Alternative to Black-Scholes for complex options
- **Portfolio Risk**: VaR and stress testing
- **Retirement Planning**: Probability of meeting income goals
- **Corporate Finance**: NPV distributions for capital budgeting

**Steps in Monte Carlo Simulation:**
1. Define the model and key variables
2. Specify probability distributions for uncertain variables
3. Generate random samples from distributions
4. Calculate outcomes for each simulation
5. Analyze distribution of results

**Example: Stock Price Simulation**
S_t = S_{t-1} × exp((μ - σ²/2)Δt + σ√Δt × Z)
Where Z is a standard normal random variable

### Factor Models

**Single-Factor Models (CAPM)**
- R_i = α_i + β_i × R_m + ε_i
- Market risk (beta) and idiosyncratic risk

**Multi-Factor Models**

**Fama-French Three-Factor Model:**
- R_i - R_f = α + β(R_m - R_f) + s×SMB + h×HML + ε
- **SMB**: Small Minus Big (size factor)
- **HML**: High Minus Low (value factor)

**Carhart Four-Factor Model:**
- Adds momentum factor (WML - Winners Minus Losers)
- Explains momentum anomaly in stock returns

**Arbitrage Pricing Theory (APT):**
- Multiple systematic risk factors
- R_i = α_i + β_1F_1 + β_2F_2 + ... + β_kF_k + ε_i
- Factors could include GDP growth, inflation, interest rates, etc.

## Alternative Investment Analysis

### Private Equity

**Investment Process:**
- **Sourcing**: Finding investment opportunities
- **Due Diligence**: Financial, operational, market analysis
- **Structuring**: Debt/equity mix, governance terms
- **Value Creation**: Operational improvements, strategic initiatives
- **Exit**: IPO, strategic sale, secondary buyout

**Valuation Methods:**
- **Comparable Company Analysis**: Public company multiples
- **Precedent Transaction Analysis**: Similar deal multiples
- **Discounted Cash Flow**: High discount rates for illiquidity
- **Asset-Based Valuation**: Liquidation or replacement cost

**Key Metrics:**
- **IRR (Internal Rate of Return)**: Annualized return including timing
- **Multiple of Money (MoM)**: Cash returned / Cash invested
- **DPI (Distributions to Paid-in)**: Actual cash returned
- **TVPI (Total Value to Paid-in)**: Realized + unrealized value

### Hedge Funds

**Strategy Categories:**

**Equity Strategies:**
- **Long/Short Equity**: Buy undervalued, sell overvalued
- **Market Neutral**: Equal long/short, beta near zero
- **Equity Market Timing**: Adjust exposure based on market views

**Event-Driven Strategies:**
- **Merger Arbitrage**: Profit from merger spreads
- **Distressed Securities**: Invest in financially troubled companies
- **Activist Investing**: Influence management to unlock value

**Relative Value Strategies:**
- **Fixed Income Arbitrage**: Exploit bond mispricings
- **Convertible Arbitrage**: Long convertible, short underlying stock
- **Volatility Trading**: Trade options based on volatility views

**Macro Strategies:**
- **Global Macro**: Trade currencies, commodities, bonds based on macro views
- **Commodity Trading Advisors (CTAs)**: Systematic trend-following
- **Currency Strategies**: Exploit currency mispricings

### Real Estate Investment

**Direct Real Estate Investment:**
- **Income Approach**: Cap rate = NOI / Property Value
- **Sales Comparison**: Comparable property transactions
- **Cost Approach**: Replacement cost minus depreciation

**Real Estate Investment Trusts (REITs):**
- **Equity REITs**: Own and operate income-producing real estate
- **Mortgage REITs**: Provide financing for real estate
- **Hybrid REITs**: Combination of equity and mortgage

**Key Metrics:**
- **Net Operating Income (NOI)**: Rental income minus operating expenses
- **Capitalization Rate**: NOI / Property Value
- **Cash-on-Cash Return**: Annual cash flow / Initial cash investment
- **Internal Rate of Return**: Including appreciation and cash flows

### Commodities

**Investment Approaches:**
- **Physical Ownership**: Direct ownership of commodities
- **Futures Contracts**: Financial exposure without physical delivery
- **ETFs/ETNs**: Diversified commodity exposure
- **Commodity-Linked Notes**: Structured products

**Contango vs. Backwardation:**
- **Contango**: Future prices > spot prices (storage costs, financing)
- **Backwardation**: Future prices < spot prices (convenience yield)
- **Roll Yield**: Return from rolling futures positions

## Emerging Trends in Finance

### Algorithmic Trading

**High-Frequency Trading (HFT):**
- Sub-millisecond execution speeds
- Market making and arbitrage strategies
- Significant infrastructure investment required

**Machine Learning Applications:**
- **Supervised Learning**: Predict returns using historical patterns
- **Unsupervised Learning**: Identify hidden patterns and clusters
- **Reinforcement Learning**: Adaptive strategies that learn from outcomes

**Natural Language Processing:**
- Sentiment analysis of news and social media
- Earnings call transcription analysis
- Regulatory filing text mining

### ESG Investing

**Environmental, Social, Governance Integration:**
- **Screening**: Exclude certain industries or practices
- **Integration**: Incorporate ESG factors into valuation
- **Shareholder Engagement**: Use ownership to influence corporate behavior
- **Impact Investing**: Target measurable social/environmental outcomes

**ESG Metrics and Ratings:**
- **Environmental**: Carbon footprint, resource usage, waste management
- **Social**: Employee relations, community impact, product safety
- **Governance**: Board composition, executive compensation, audit quality

### Cryptocurrency and Digital Assets

**Blockchain Technology:**
- Distributed ledger technology
- Immutable transaction records
- Smart contracts for automated execution

**Valuation Challenges:**
- No underlying cash flows for pure cryptocurrencies
- Network effects and adoption metrics
- Regulatory uncertainty and compliance costs
- Extreme volatility and liquidity concerns

**DeFi (Decentralized Finance):**
- Automated market makers (AMMs)
- Yield farming and liquidity mining
- Decentralized lending and borrowing protocols

### Robo-Advisors and FinTech

**Automated Portfolio Management:**
- Algorithm-based asset allocation
- Automatic rebalancing and tax-loss harvesting
- Lower fees and minimum investments

**Fractional Investing:**
- Own fractions of expensive stocks/assets
- Increased diversification for small investors
- Lower barriers to entry for investment

Advanced quantitative methods and alternative investments provide sophisticated tools for risk management and return enhancement, but require deep understanding of their complexities and limitations.`
        },
        {
          id: "alternative-investments",
          title: "Alternative Investments Overview",
          description: "Private equity, hedge funds, REITs, and commodities",
          type: "video",
          duration: 30
        },
        {
          id: "portfolio-simulation",
          title: "Advanced Portfolio Simulation",
          description: "Monte Carlo simulation for portfolio planning and risk analysis",
          type: "interactive",
          duration: 25,
          interactiveData: {
            type: "monte_carlo_simulation",
            title: "Portfolio Monte Carlo Simulation",
            instructions: "Run Monte Carlo simulations to analyze portfolio outcomes under different scenarios.",
            portfolioOptions: [
              { name: "Conservative", stocks: 0.3, bonds: 0.6, alternatives: 0.1 },
              { name: "Moderate", stocks: 0.6, bonds: 0.3, alternatives: 0.1 },
              { name: "Aggressive", stocks: 0.8, bonds: 0.1, alternatives: 0.1 }
            ],
            parameters: {
              initialInvestment: 100000,
              monthlyContribution: 1000,
              timeHorizon: 30,
              simulations: 10000,
              confidenceIntervals: [10, 50, 90]
            },
            expectedOutput: "Probability distributions of portfolio outcomes with confidence intervals"
          }
        }
      ]
    }
  ],

  assignments: [
    {
      id: "financial-analysis-project",
      title: "Company Financial Analysis Project",
      description: "Complete financial analysis of a public company including ratio analysis, valuation, and investment recommendation",
      type: "project",
      dueWeeks: 4,
      maxScore: 100,
      instructions: `# Company Financial Analysis Project

## Objective
Conduct a comprehensive financial analysis of a publicly-traded company and provide an investment recommendation.

## Requirements
1. **Company Selection**: Choose a large-cap company from S&P 500
2. **Financial Statement Analysis**: Analyze 5 years of financial statements
3. **Ratio Analysis**: Calculate and interpret key financial ratios
4. **Peer Comparison**: Compare to 2-3 industry competitors
5. **Valuation**: Use DCF and relative valuation methods
6. **Investment Recommendation**: Buy, Hold, or Sell with price target

## Deliverables
- 15-20 page analysis report
- Excel financial model with calculations
- PowerPoint presentation (10-15 slides)

## Grading Criteria
- Financial statement analysis (25%)
- Ratio calculations and interpretation (25%)
- Valuation methodology and accuracy (30%)
- Investment recommendation quality (20%)`,
      submissionFormat: "pdf",
      resources: [
        "SEC EDGAR database for 10-K filings",
        "Yahoo Finance for market data",
        "Industry reports from course library"
      ]
    },
    {
      id: "portfolio-construction",
      title: "Portfolio Construction and Optimization",
      description: "Build and optimize a diversified investment portfolio using modern portfolio theory",
      type: "project", 
      dueWeeks: 3,
      maxScore: 100,
      instructions: `# Portfolio Construction Project

## Objective
Design and optimize an investment portfolio for a specific investor profile using modern portfolio theory principles.

## Investor Profile
- Age: 35 years old
- Investment horizon: 30 years until retirement
- Risk tolerance: Moderate
- Initial investment: $100,000
- Monthly contributions: $2,000

## Requirements
1. **Asset Allocation**: Design strategic asset allocation
2. **Security Selection**: Choose specific ETFs/mutual funds
3. **Risk Analysis**: Calculate portfolio risk metrics
4. **Optimization**: Use mean-variance optimization
5. **Rebalancing**: Develop rebalancing strategy
6. **Performance Projection**: Monte Carlo simulation

## Deliverables
- Portfolio proposal document (10-12 pages)
- Excel model with optimization calculations
- Risk/return analysis charts

## Grading Criteria
- Asset allocation rationale (20%)
- Security selection and diversification (25%)
- Risk analysis accuracy (25%)
- Optimization methodology (30%)`,
      submissionFormat: "excel",
      resources: [
        "Morningstar ETF database",
        "Portfolio optimization templates",
        "Historical return data"
      ]
    },
    {
      id: "options-trading-strategy",
      title: "Options Trading Strategy Analysis",
      description: "Design and analyze a comprehensive options trading strategy",
      type: "case-study",
      dueWeeks: 2,
      maxScore: 75,
      instructions: `# Options Strategy Case Study

## Scenario
Stock XYZ is trading at $100. Earnings are announced in 3 weeks. 
Historical volatility: 25%
Implied volatility: 35%
Current market trend: Neutral with high uncertainty

## Requirements
1. **Strategy Selection**: Choose appropriate options strategy
2. **Risk Analysis**: Calculate maximum profit, loss, breakeven
3. **Greeks Analysis**: Analyze delta, gamma, theta, vega exposure
4. **Scenario Analysis**: Test strategy under different outcomes
5. **Exit Strategy**: Define when and how to close positions

## Deliverables
- Strategy analysis report (8-10 pages)
- Options calculator spreadsheet
- Profit/loss diagrams

## Grading Criteria
- Strategy appropriateness (25%)
- Risk calculations accuracy (30%)
- Greeks analysis (25%) 
- Scenario planning (20%)`,
      submissionFormat: "pdf",
      resources: [
        "Options pricing calculator",
        "Volatility data",
        "Strategy comparison templates"
      ]
    }
  ],

  finalExam: {
    id: "finance-mastery-final",
    title: "Finance Mastery Final Exam",
    description: "Comprehensive exam covering all finance concepts and analysis techniques",
    timeLimit: 90,
    passingScore: 85,
    attempts: 2,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "In a DCF model, what does WACC represent?",
        options: [
          "Weighted Average Cost of Capital - the required rate of return",
          "Working Asset Capital Calculation",
          "Weighted Asset Cost Calculation", 
          "Working Average Cash Conversion"
        ],
        correctAnswer: 0,
        explanation: "WACC is the weighted average cost of capital, representing the required rate of return for the company's financing."
      },
      {
        id: "final-q2", 
        type: "multiple-choice",
        question: "Which ratio best measures a company's ability to pay short-term obligations?",
        options: [
          "Debt-to-equity ratio",
          "Current ratio",
          "Return on equity",
          "Price-to-earnings ratio"
        ],
        correctAnswer: 1,
        explanation: "The current ratio (current assets / current liabilities) measures liquidity and ability to pay short-term debts."
      },
      {
        id: "final-q3",
        type: "multiple-choice", 
        question: "What is the main benefit of diversification in portfolio management?",
        options: [
          "Increases expected returns",
          "Reduces risk without necessarily reducing expected returns",
          "Eliminates all investment risk",
          "Guarantees positive returns"
        ],
        correctAnswer: 1,
        explanation: "Diversification reduces portfolio risk through combining uncorrelated assets without necessarily reducing expected returns."
      },
      {
        id: "final-q4",
        type: "multiple-choice",
        question: "According to the Efficient Market Hypothesis, which form suggests that all public information is reflected in stock prices?",
        options: [
          "Weak form",
          "Semi-strong form", 
          "Strong form",
          "Ultra-strong form"
        ],
        correctAnswer: 1,
        explanation: "Semi-strong form EMH states that all publicly available information is quickly incorporated into stock prices."
      },
      {
        id: "final-q5",
        type: "multiple-choice",
        question: "What does a negative correlation coefficient between two assets indicate?",
        options: [
          "The assets move in the same direction",
          "The assets move in opposite directions",
          "The assets are unrelated",
          "One asset will always outperform"
        ],
        correctAnswer: 1,
        explanation: "A negative correlation means when one asset increases in value, the other tends to decrease, providing diversification benefits."
      },
      {
        id: "final-q6",
        type: "multiple-choice",
        question: "In bond analysis, duration measures:",
        options: [
          "Time to maturity",
          "Credit quality",
          "Interest rate sensitivity",
          "Default probability"
        ],
        correctAnswer: 2,
        explanation: "Duration measures a bond's price sensitivity to interest rate changes - higher duration means greater price volatility."
      },
      {
        id: "final-q7", 
        type: "multiple-choice",
        question: "Which of the following is a characteristic of a growth stock?",
        options: [
          "High dividend yield",
          "Low P/E ratio",
          "High price-to-book ratio",
          "Low beta"
        ],
        correctAnswer: 2,
        explanation: "Growth stocks typically trade at high price-to-book ratios due to expectations of future earnings growth."
      },
      {
        id: "final-q8",
        type: "multiple-choice", 
        question: "What is the primary purpose of a stop-loss order?",
        options: [
          "To guarantee profits",
          "To limit potential losses",
          "To increase position size",
          "To earn dividends"
        ],
        correctAnswer: 1,
        explanation: "A stop-loss order is designed to limit an investor's loss on a position by triggering a sale when the price falls to a predetermined level."
      },
      {
        id: "final-q9",
        type: "multiple-choice",
        question: "In options trading, what happens to an option's time value as expiration approaches?",
        options: [
          "It increases exponentially",
          "It remains constant",
          "It decays to zero",
          "It becomes negative"
        ],
        correctAnswer: 2,
        explanation: "Time value (theta) decays as expiration approaches, eventually reaching zero at expiration when only intrinsic value remains."
      },
      {
        id: "final-q10",
        type: "multiple-choice",
        question: "What is the key difference between active and passive investment management?",
        options: [
          "Active costs more but always performs better",
          "Active tries to beat the market, passive tries to match it",
          "Passive management requires more research",
          "Active management has lower fees"
        ],
        correctAnswer: 1,
        explanation: "Active management attempts to outperform market benchmarks through security selection and timing, while passive management aims to match market returns."
      },
      {
        id: "final-q11",
        type: "multiple-choice",
        question: "Which financial statement would you examine to assess a company's profitability over time?",
        options: [
          "Balance Sheet",
          "Income Statement",
          "Cash Flow Statement", 
          "Statement of Equity"
        ],
        correctAnswer: 1,
        explanation: "The Income Statement (P&L) shows revenues, expenses, and profits over a specific time period, making it ideal for assessing profitability trends."
      },
      {
        id: "final-q12",
        type: "multiple-choice",
        question: "What does a high debt-to-equity ratio typically indicate?",
        options: [
          "Strong profitability",
          "High financial leverage and risk",
          "Excellent liquidity",
          "Conservative management"
        ],
        correctAnswer: 1,
        explanation: "A high debt-to-equity ratio indicates the company uses significant debt financing, which increases financial leverage and potential risk."
      },
      {
        id: "final-q13", 
        type: "multiple-choice",
        question: "In behavioral finance, what is loss aversion?",
        options: [
          "Avoiding all investment losses",
          "Feeling losses more strongly than equivalent gains", 
          "Preferring guaranteed returns",
          "Diversifying to reduce risk"
        ],
        correctAnswer: 1,
        explanation: "Loss aversion is the psychological tendency where people feel the pain of losses roughly twice as strongly as the pleasure of equivalent gains."
      },
      {
        id: "final-q14",
        type: "multiple-choice",
        question: "What is the main advantage of dollar-cost averaging?",
        options: [
          "Guarantees higher returns",
          "Eliminates market risk",
          "Reduces timing risk through regular investments",
          "Provides immediate diversification"
        ],
        correctAnswer: 2,
        explanation: "Dollar-cost averaging reduces timing risk by spreading purchases over time, potentially buying more shares when prices are low and fewer when high."
      },
      {
        id: "final-q15",
        type: "multiple-choice",
        question: "Which metric is most useful for comparing companies of different sizes in the same industry?",
        options: [
          "Net income",
          "Total revenue", 
          "Return on equity (ROE)",
          "Total assets"
        ],
        correctAnswer: 2,
        explanation: "ROE is a percentage-based ratio that allows meaningful comparison of profitability across companies of different sizes."
      },
      {
        id: "final-q16",
        type: "multiple-choice",
        question: "What is the primary risk of investing in emerging market bonds?",
        options: [
          "Interest rate risk",
          "Currency and political risk",
          "Inflation risk",
          "Liquidity risk"
        ],
        correctAnswer: 1,
        explanation: "Emerging market bonds face significant currency risk (exchange rate fluctuations) and political risk (government instability, policy changes)."
      },
      {
        id: "final-q17",
        type: "multiple-choice",
        question: "In corporate finance, what does the pecking order theory suggest?",
        options: [
          "Companies should always maximize debt",
          "Companies prefer internal financing, then debt, then equity",
          "Equity is always the cheapest form of financing",
          "Capital structure doesn't matter"
        ],
        correctAnswer: 1,
        explanation: "Pecking order theory suggests companies prefer internal financing first, then debt, and equity last due to information asymmetry costs."
      },
      {
        id: "final-q18", 
        type: "multiple-choice",
        question: "What characterizes a contango market in commodities futures?",
        options: [
          "Spot prices higher than futures prices",
          "Futures prices higher than spot prices",
          "All prices are equal",
          "Prices are declining"
        ],
        correctAnswer: 1,
        explanation: "In contango, futures prices are higher than spot prices, often due to storage costs and financing costs exceeding convenience yield."
      },
      {
        id: "final-q19",
        type: "multiple-choice",
        question: "What is the main purpose of stress testing in risk management?",
        options: [
          "To predict exact future losses",
          "To test portfolio performance under extreme scenarios",
          "To calculate expected returns",
          "To determine optimal asset allocation"
        ],
        correctAnswer: 1,
        explanation: "Stress testing evaluates how a portfolio might perform under extreme market conditions or adverse scenarios that go beyond normal expectations."
      },
      {
        id: "final-q20",
        type: "multiple-choice",
        question: "Which factor is NOT typically included in ESG (Environmental, Social, Governance) analysis?",
        options: [
          "Carbon emissions",
          "Board diversity",
          "Share price performance",
          "Employee safety"
        ],
        correctAnswer: 2,
        explanation: "ESG analysis focuses on environmental impact, social responsibility, and governance quality, not direct financial metrics like share price performance."
      }
    ]
  },

  quizzes: []
};